"use client";

import { trimSvgMarkupViewBox } from "@/lib/trim-svg-viewbox";
import { useEffect, useState } from "react";

const trimmedUrlCache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

function urlLooksLikeSvg(url: string): boolean {
  return url.toLowerCase().includes(".svg");
}

async function resolveTrimmedSvgLogoUrl(url: string): Promise<string> {
  const cached: string | undefined = trimmedUrlCache.get(url);
  if (cached !== undefined) return cached;

  const pending: Promise<string> | undefined = inflight.get(url);
  if (pending !== undefined) return pending;

  const job: Promise<string> = (async (): Promise<string> => {
    try {
      const response: Response = await fetch(url);
      if (!response.ok) return url;
      const contentType: string = response.headers.get("content-type") ?? "";
      const text: string = await response.text();
      const isSvg: boolean =
        contentType.includes("svg") ||
        text.trimStart().startsWith("<svg") ||
        urlLooksLikeSvg(url);
      if (!isSvg) {
        trimmedUrlCache.set(url, url);
        return url;
      }
      const trimmed: string | null = trimSvgMarkupViewBox(text);
      if (trimmed === null) {
        trimmedUrlCache.set(url, url);
        return url;
      }
      const blobUrl: string = URL.createObjectURL(
        new Blob([trimmed], { type: "image/svg+xml" }),
      );
      trimmedUrlCache.set(url, blobUrl);
      return blobUrl;
    } catch {
      trimmedUrlCache.set(url, url);
      return url;
    } finally {
      inflight.delete(url);
    }
  })();

  inflight.set(url, job);
  return job;
}

/**
 * Returns a logo URL with SVG viewBox padding removed (cached).
 * Non-SVG URLs pass through unchanged.
 */
export function useTrimmedSvgLogoUrl(url: string | null): string | null {
  const [resolved, setResolved] = useState<string | null>(() => {
    if (url === null) return null;
    return trimmedUrlCache.get(url) ?? url;
  });

  useEffect(() => {
    if (url === null) {
      setResolved(null);
      return;
    }
    const cached: string | undefined = trimmedUrlCache.get(url);
    if (cached !== undefined) {
      setResolved(cached);
      return;
    }
    if (!urlLooksLikeSvg(url)) {
      setResolved(url);
      return;
    }
    setResolved(url);
    let cancelled = false;
    void resolveTrimmedSvgLogoUrl(url).then((next: string) => {
      if (!cancelled) setResolved(next);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return resolved;
}

/** Trimmed logo URL plus `final`: false while a swap to the trimmed copy is still coming. */
export function useTrimmedSvgLogo(url: string | null): {
  readonly src: string | null;
  readonly final: boolean;
} {
  const src = useTrimmedSvgLogoUrl(url);
  const final = url === null || !urlLooksLikeSvg(url) || trimmedUrlCache.get(url) === src;
  return { src, final };
}
